import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGraduationCap, FaCalendar, FaMapMarkerAlt, FaAward, FaChevronLeft, FaChevronRight, FaEye } from 'react-icons/fa';
import { education } from '../data/portfolioData';
import AnimatedSection from './AnimatedSection';
import { useTranslation } from 'react-i18next';
import { localizedItem } from '../locales/contentTranslations';

const EducationSlider = () => {
  const { t, i18n } = useTranslation();
  const [startIndex, setStartIndex] = useState(0);
  const [selectedEdu, setSelectedEdu] = useState(null);
  
  const itemsPerPage = 2; // Education ima samo 2 stvari, pa prikazujemo obje
  const totalPages = Math.ceil(education.length / itemsPerPage);
  const currentPage = Math.floor(startIndex / itemsPerPage);
  
  const localizedEducation = education.map(item => localizedItem('education', item, i18n));
  const currentEdu = selectedEdu
  ? localizedEducation.find(item => item.id === selectedEdu.id)
  : null;
  const visibleEducation = localizedEducation.slice(startIndex, startIndex + itemsPerPage);

  const nextSlide = () => {
    if (startIndex + itemsPerPage < education.length) {
      setStartIndex(startIndex + itemsPerPage);
    } else {
      setStartIndex(0);
    }
  };

  const prevSlide = () => {
    if (startIndex - itemsPerPage >= 0) {
      setStartIndex(startIndex - itemsPerPage);
    } else {
      setStartIndex(Math.max(0, education.length - itemsPerPage));
    }
  };

  const openModal = (edu) => {
    setSelectedEdu(edu);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedEdu(null);
    document.body.style.overflow = 'auto';
  };

  // Boje za različite nivoe obrazovanja
  const getGradientColor = (index) => {
    const gradients = [
      'from-emerald-500/20 to-teal-500/20',
      'from-blue-500/20 to-cyan-500/20',
      'from-purple-500/20 to-pink-500/20'
    ];
    return gradients[index % gradients.length];
  };

  return (
    <section id="education" className="py-16 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex items-center gap-3 justify-center mb-4">
            <FaGraduationCap className="text-primary text-3xl md:text-4xl" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center">
              {t("education.title")}
            </h2>
          </div>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12 text-sm md:text-base">
            {t('education.subtitle')}
          </p>
        </AnimatedSection>

        {/* Slajder */}
        <div className="relative max-w-5xl mx-auto">
          {/* Navigation dugmad - samo ako ima više od itemsPerPage */}
          {education.length > itemsPerPage && (
            <>
              <button
                onClick={prevSlide}
                className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition hover-glow"
              >
                <FaChevronLeft size={20} />
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition hover-glow"
              >
                <FaChevronRight size={20} />
              </button>
            </>
          )}

          {/* Grid sa edukacijom */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AnimatePresence mode="wait">
              {visibleEducation.map((edu, index) => (
                <motion.div
                  key={edu.id}
                  initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                  transition={{ duration: 0.4 }}
                  whileHover={{ y: -8 }}
                  className="group cursor-pointer h-full"
                  onClick={() => openModal(edu)}
                >
                  <div className={`relative glass-effect rounded-2xl overflow-hidden h-full transition-all duration-300 hover-glow bg-gradient-to-br ${getGradientColor(index)}`}>
                    {/* Header sa periodom */}
                    <div className="relative p-5 pb-0">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2 text-primary text-sm">
                          <FaCalendar size={12} />
                          <span>{edu.period}</span>
                        </div>
                        <div className="w-12 h-12 rounded-full glass-effect flex items-center justify-center text-2xl">
                          {edu.icon || '🎓'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-5 pt-3">
                      <h3 className="text-xl md:text-2xl font-bold group-hover:text-primary transition mb-2">
                        {edu.degree}
                      </h3>
                      <p className="text-primary text-base mb-3">{edu.institution}</p>
                      
                      <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                        {edu.description}
                      </p>
                      
                      {/* Achievements */}
                      {edu.achievements && edu.achievements.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {edu.achievements.slice(0, 2).map((achievement, i) => (
                            <span key={i} className="text-xs px-2 py-1 glass-effect rounded-full flex items-center gap-1">
                              <FaAward size={10} /> {achievement}
                            </span>
                          ))}
                          {edu.achievements.length > 2 && (
                            <span className="text-xs px-2 py-1 glass-effect rounded-full">
                              +{edu.achievements.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-3 flex items-center gap-2 text-primary text-sm group-hover:gap-3 transition-all">
                        <FaEye size={12} />
                        <span>{t('education.viewDetails')}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Paginacija - samo ako ima više od itemsPerPage */}
          {education.length > itemsPerPage && (
            <>
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setStartIndex(idx * itemsPerPage)}
                    className={`transition-all duration-300 ${
                      currentPage === idx 
                        ? 'w-8 h-2 bg-primary rounded-full' 
                        : 'w-2 h-2 bg-gray-600 rounded-full hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              <div className="text-center mt-4">
                <p className="text-gray-500 text-sm">
                  {t('education.showing')} {startIndex + 1} - {Math.min(startIndex + itemsPerPage, education.length)} / {education.length} {t('education.institutions')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal za detalje edukacije */}
      <AnimatePresence>
        {currentEdu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              className="relative bg-gradient-to-br from-darker to-dark rounded-2xl max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {currentEdu.image && (
  <div className="relative h-48 md:h-64 overflow-hidden rounded-t-2xl">
    <img
      src={currentEdu.image}
      alt={currentEdu.institution}
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent" />
  </div>
)}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-20 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                ✕
              </button>

              <div className="p-6 md:p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl glass-effect flex items-center justify-center text-3xl">
                    {currentEdu.icon || '🎓'}
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold">{currentEdu.degree}</h3>
                    <p className="text-primary text-lg">{currentEdu.institution}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-6 text-gray-400 text-sm">
                  <div className="flex items-center gap-1">
                    <FaCalendar size={12} />
                    <span>{currentEdu.period}</span>
                  </div>
                </div>

                <p className="text-gray-300 leading-relaxed mb-6">
                  {currentEdu.description}
                </p>

                {currentEdu.achievements && currentEdu.achievements.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                      <FaAward /> {t('education.keyAchievements')}
                    </h4>
                    <div className="space-y-2">
                      {currentEdu.achievements.map((achievement, i) => (
                        <div key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                          <span className="text-primary mt-1">▹</span>
                          <span>{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default EducationSlider;